import { format, parse, parseISO } from 'date-fns';
import { RiskItem } from 'src/app/api';
import { QuotationSummaryResponse } from '../models/model/concretes/shared/quotation-summary.model';
import { DeclarationState } from 'src/app/core/declaration.store';
import {
  NOM_ANI,
  TYPE_ANI,
  RACE_ANI,
  SEX_ANI,
  DATENAISS_ANI,
  PUC_ANI,
  ID_ANI,
  VAC_ANI,
  ANT_ANI,
  MAL_ANI,
  REM_TYPE
} from '../models/model/concretes/shared/risk-item-values-ani.model';

// Fonction qui génère le RiskItems avec `store` passé en paramètre
function getRiskItems(store: any): Record<string, RiskItem> {
  const address = store.declaration().addressAutocomplete;

  return {
    ANI: {
      order: 1,
      typeCode: 'ANI',
      name: 'Animal',
      fields: {
        NOM_ANI: store.declaration.name(),
        TYPE_ANI: store.declaration.type(),
        RACE_ANI: store.declaration.race()?.shortLabel ?? store.declaration.race(),
        DATENAISS_ANI: format_date(store.declaration.birth_date()),
        SEX_ANI: store.declaration.sexe(),
        PUC_ANI: store.declaration.chip_tatoo(),
        ID_ANI: store.declaration.identification(),
        VAC_ANI: store.declaration.vaccines(),
        ANT_ANI: store.declaration.medical_history(),
        MAL_ANI: store.declaration.diseaces(),
        REM_TYPE: store.declaration.cover_level()
      }
    },
    PER: {
      order: 2,
      typeCode: 'PER',
      name: 'Personne',
      fields: {
        ADR_RISQUE:
          address?.streetNumber && address?.street
            ? `${address.streetNumber} ${address.street}`
            : address?.place,
        ADR_CODE_POSTAL: address?.postalCode,
        ADR_VILLE: address?.city
      }
    }
  };
}

// Fonction qui génère le DeclarationState avec `QuotationSummaryResponse` passé en paramètre
function getDeclarationStateFromQuotationSummary(
  quotationSummary: QuotationSummaryResponse
): DeclarationState {
  const birthDate = quotationSummary.riskItemInformations.values.find(v => v.code === DATENAISS_ANI)?.value ?? '';
  return {
    name: quotationSummary.riskItemInformations.values.find(v => v.code === NOM_ANI)?.value ?? '',
    addressAutocomplete: null, // À définir selon le contexte
    address: quotationSummary.clientInformations.address1,
    postalCode: quotationSummary.clientInformations.zipCode,
    city: quotationSummary.clientInformations.addressCity,
    country: null, // À définir selon le contexte
    type: quotationSummary.riskItemInformations.values.find(v => v.code === TYPE_ANI)?.value ?? '',
    race: {
      shortLabel:
        quotationSummary.riskItemInformations.values.find(v => v.code === RACE_ANI)?.value ?? ''
    },
    sexe: quotationSummary.riskItemInformations.values.find(v => v.code === SEX_ANI)?.value ?? '',
    birth_date: format(parse(birthDate, 'yyyy-MM-dd', new Date()),'dd/MM/yyyy HH:mm:ss'),
    chip_tatoo:
      quotationSummary.riskItemInformations.values.find(v => v.code === PUC_ANI)?.value === 'True',
    identification:
      quotationSummary.riskItemInformations.values.find(v => v.code === ID_ANI)?.value ?? '',
    vaccines:
      quotationSummary.riskItemInformations.values.find(v => v.code === VAC_ANI)?.value === 'True',
    medical_history:
      quotationSummary.riskItemInformations.values.find(v => v.code === ANT_ANI)?.value ?? '',
    diseaces: quotationSummary.riskItemInformations.values
      .filter(v => v.code === MAL_ANI)
      .map(v => v.value),
    cover_level:
      quotationSummary.riskItemInformations.values.find(v => v.code === REM_TYPE)?.value === 'True',
    effect_date: format(
      parse(quotationSummary.requestedStartEffectiveDate, 'yyyy-MM-dd', new Date()),
      'dd/MM/yyyy HH:mm:ss'
    )
  };
}

// Fonction qui retourne un RiskItem spécifique
export function getRiskItem(store: any, key: string): RiskItem | undefined {
  const riskItems = getRiskItems(store);
  return riskItems[key]; // Retourne l'objet si trouvé, sinon `undefined`
}

export function getDeclarationState(quotationSummary: QuotationSummaryResponse): DeclarationState {
  return getDeclarationStateFromQuotationSummary(quotationSummary);
}

function format_date(dateValue: string | Date | null): string {
  if (dateValue instanceof Date) {
    return format(dateValue, 'yyyy-MM-dd');
  }

  if (typeof dateValue !== 'string' || dateValue.trim() === '') {
    throw new Error('La date de naissance de l’animal est absente ou invalide.');
  }

  const date = /^\d{4}-\d{2}-\d{2}/.test(dateValue)
    ? parseISO(dateValue)
    : parse(dateValue.slice(0, 10), 'dd/MM/yyyy', new Date());

  return format(date, 'yyyy-MM-dd');
}
